
import { Card, CardContent } from "@/components/ui/card";
import DashboardLayout from "@/components/DashboardLayout";
import ExpenseManager from "@/components/ExpenseManager";
import SubscriptionGuard from "@/components/SubscriptionGuard";
import ReportsSummaryCards from "@/components/ReportsSummaryCards";
import ReportsCharts from "@/components/ReportsCharts";
import { useEtsyIntegration } from "@/hooks/useEtsyIntegration";
import { useEtsyOrders } from "@/hooks/useEtsyOrders";
import { useReportsAnalytics } from "@/hooks/useReportsAnalytics";
import { useSubscriptionStatus } from "@/hooks/useSubscriptionStatus";
import { useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";

interface Expense {
  id: string;
  description: string;
  amount: number;
  category: string;
  expense_date: string;
  notes?: string;
}

const Reports = () => {
  const { hasActiveSubscription, isLoading: isSubscriptionLoading } = useSubscriptionStatus();
  const { 
    etsyOrders, 
    fetchEtsyOrders, 
    isFetchingOrders 
  } = useEtsyOrders(hasActiveSubscription, isSubscriptionLoading);
  
  const { checkEtsyConnection } = useEtsyIntegration();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  
  const { analytics, formatPrice } = useReportsAnalytics(etsyOrders, expenses);

  useEffect(() => {
    // Check if connected and load orders only if has subscription
    if (hasActiveSubscription) {
      checkEtsyConnection().then(isConnected => {
        if (isConnected && etsyOrders.length === 0) {
          fetchEtsyOrders();
        }
      });
    }
  }, [hasActiveSubscription]);

  const handleExpensesChange = (newExpenses: Expense[]) => {
    setExpenses(newExpenses);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Reports & Analytics</h1>
            <p className="text-gray-600 mt-2">Comprehensive business insights with expense tracking and profit analysis.</p>
          </div>
          {isFetchingOrders && hasActiveSubscription && (
            <div className="flex items-center gap-2 text-gray-500">
              <RefreshCw className="h-4 w-4 animate-spin" />
              <span>Updating data...</span>
            </div>
          )}
        </div>

        <SubscriptionGuard feature="reports and analytics">
          {etsyOrders.length === 0 && !isFetchingOrders ? (
            <>
              <ExpenseManager onExpensesChange={handleExpensesChange} />
              <Card>
                <CardContent className="py-8">
                  <div className="text-center">
                    <p className="text-gray-500">No order data available for reports.</p>
                    <p className="text-sm text-gray-400 mt-2">
                      Connect your Etsy shop and sync your orders to see detailed analytics.
                    </p>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <>
              {/* Summary Cards */}
              <ReportsSummaryCards analytics={analytics} formatPrice={formatPrice} />

              {/* Expense Manager */}
              <ExpenseManager onExpensesChange={handleExpensesChange} />

              {/* Charts */}
              <ReportsCharts analytics={analytics} formatPrice={formatPrice} />
            </>
          )}
        </SubscriptionGuard>
      </div>
    </DashboardLayout>
  );
};

export default Reports;
